import React, { useEffect, useState } from "react";
import { api, formatINR, formatDateTime } from "../lib/api";
import { KPICard, PageHeader, LoadingRows } from "../components/common";
import { Card } from "../components/ui/card";
import { TID } from "../constants/testIds";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Building2, Store, Battery, PackageCheck, PackageSearch, CreditCard, HardHat, Wrench,
  ShieldAlert, IndianRupee, TrendingUp, Factory,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#A855F7", "#EF4444", "#0EA5E9"];

export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data));
    api.get("/dashboard/charts").then((r) => setCharts(r.data));
    api.get("/dashboard/recent-activities").then((r) => setRecent(r.data.items));
  }, []);

  if (!kpis) return <div className="space-y-4"><LoadingRows rows={4} /></div>;

  const cards = [
    { key: "brands", label: "Total Brands", value: kpis.total_brands, icon: Building2, accent: "blue", tid: TID.kpiBrands },
    { key: "outlets", label: "Total Outlets", value: kpis.total_outlets, icon: Store, accent: "emerald", tid: TID.kpiOutlets },
    { key: "assets", label: "Total Assets", value: kpis.total_assets, icon: Battery, accent: "fuchsia", tid: TID.kpiAssets },
    { key: "installed", label: "Installed Assets", value: kpis.installed_assets, icon: PackageCheck, accent: "emerald" },
    { key: "inventory", label: "Available Inventory", value: kpis.available_inventory, icon: PackageSearch, accent: "slate" },
    { key: "subs", label: "Active Subscriptions", value: kpis.active_subscriptions, icon: CreditCard, accent: "blue" },
    { key: "installs", label: "Pending Installations", value: kpis.pending_installations, icon: HardHat, accent: "amber" },
    { key: "maint", label: "Pending Maintenance", value: kpis.pending_maintenance, icon: Wrench, accent: "amber" },
    { key: "warranty", label: "Warranty Claims", value: kpis.warranty_claims, icon: ShieldAlert, accent: "rose" },
    { key: "mrr", label: "Monthly Revenue", value: formatINR(kpis.monthly_revenue), icon: IndianRupee, accent: "emerald", tid: TID.kpiMRR },
    { key: "arr", label: "Annual Revenue", value: formatINR(kpis.annual_revenue), icon: TrendingUp, accent: "emerald" },
    { key: "mfrs", label: "Active Manufacturers", value: kpis.active_manufacturers, icon: Factory, accent: "slate" },
  ].filter((c) => user.role === "super_admin" || !["mfrs", "inventory"].includes(c.key));

  return (
    <>
      <PageHeader
        title={`Welcome back, ${user.name?.split(" ")[0] || "there"}`}
        description={user.role === "super_admin" ? "Platform-wide operations at a glance." : "Your operations at a glance."}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {cards.map((c) => (
          <KPICard key={c.key} label={c.label} value={c.value} icon={c.icon} accent={c.accent} testid={c.tid} />
        ))}
      </div>

      {charts && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 lg:col-span-2 rounded-xl">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-heading font-medium">Revenue trend</h3>
              <div className="text-xs text-muted-foreground">Last 6 months (paid invoices)</div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatINR(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 4, fill: "#2563EB" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 rounded-xl">
            <h3 className="font-heading font-medium mb-3">Subscription mix</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={charts.subscription_distribution} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                    {charts.subscription_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 rounded-xl">
            <h3 className="font-heading font-medium mb-3">Assets by manufacturer</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.assets_by_manufacturer}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#10B981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 lg:col-span-2 rounded-xl">
            <h3 className="font-heading font-medium mb-3">Outlets by city</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.assets_by_city}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="city" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#A855F7" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      <Card className="p-4 rounded-xl">
        <h3 className="font-heading font-medium mb-3">Recent activity</h3>
        {recent.length === 0 ? (
          <div className="text-sm text-muted-foreground">No recent activity.</div>
        ) : (
          <div className="divide-y">
            {recent.slice(0, 10).map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2.5 text-sm">
                <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="truncate"><span className="font-medium capitalize">{r.action}</span> · {r.entity}{r.entity_id ? ` #${r.entity_id.slice(0, 6)}` : ""}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.user_email || "system"} — {r.details}</div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0">{formatDateTime(r.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
